"use client"

import { useEffect, useState } from "react"
import Link from "next/link"
import Image from "next/image"
import { Button } from "@/components/ui/button"
import { CheckCircle, ArrowRight, MapPin, Mic, ArrowLeft, X, Menu } from "lucide-react"
import { WaveSeparator } from "@/components/wave-separator"
import { FloatingBadge, MagneticButton } from "@/components/micro-animations"
import { useAnalytics } from "@/hooks/use-analytics"
import { triggerConfetti } from "@/components/confetti-effect"

export default function ServicesProPage() {
  const { trackSectionView, trackEvent } = useAnalytics()

    const [mobileMenuOpen, setMobileMenuOpen] = useState(false)


  // Suivre la vue de la page
  useEffect(() => {
    trackSectionView("service_pour_les_professionnels_page")

    trackEvent({
      action: "page_view",
      category: "navigation",
      label: "Service Pour les Professionnels",
      page_type: "service",
      timestamp: new Date().toISOString(),
    })
  }, [trackSectionView, trackEvent])

  const scrollToNextSection = () => {
    const studioElement = document.getElementById("services")
    if (studioElement) {
      studioElement.scrollIntoView({ behavior: "smooth" })
    }
  }
  const scrollToContactSection = () => {
    window.location.href = "/contact"
  }

  return (
    <div className="flex min-h-screen flex-col bg-podspace-dark text-podspace-white">
      {/* Header avec bouton retour */}
<header className="sticky top-0 z-40 w-full border-b border-podspace-orange/10 bg-podspace-dark/95 backdrop-blur supports-[backdrop-filter]:bg-podspace-dark/60">
          <div className="container flex h-16 items-center justify-between px-4 md:px-6">
          <div className="flex gap-2 items-center text-xl font-bold">
              <span className="relative flex items-center ">
                P
                <img alt="logo-podspace" src="/logo.svg" width="15px"/>
                <span>dspace</span>
                <span className="text-podspace-orange">.</span>
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-podspace-orange transition-all duration-300 group-hover:w-full"></span>
              </span>
            </div>
            <div className="flex items-center">
              <nav className="hidden md:flex items-center space-x-1" aria-label="Navigation principale">
                <Link
                  href="/#studios"
                  className="text-sm font-medium transition-colors hover:text-podspace-orange px-3 py-2 border-hover"
                >
                  Studios
                </Link>
                <Link
                  href="/service-pour-les-professionnels"
                  className="text-sm font-medium transition-colors hover:text-podspace-orange px-3 py-2 border-hover"
                >
                  Service pour les Pros
                </Link>
                <Link
                  href="/fonctionnement-podspace"
                  className="text-sm font-medium transition-colors hover:text-podspace-orange px-3 py-2 border-hover"
                >
                  Comment ça marche ?
                </Link>
                <Link
                  href="/#pricing"
                  className="text-sm font-medium transition-colors hover:text-podspace-orange px-3 py-2 border-hover"
                >
                  Tarifs
                </Link>
                {/* <Link
                  href="/creez-avec-carl"
                  className="text-sm font-medium transition-colors hover:text-podspace-orange px-3 py-2 border-hover"
                >
                  Créez avec Carl
                </Link> */}

              </nav>
              <MagneticButton
                className="ml-4 bg-podspace-orange hover:bg-podspace-orange/90 text-podspace-white rounded-md px-4 py-2 font-medium transition-all duration-300 hover:shadow-[0_0_15px_rgba(255,78,0,0.5)]"
                onClick={() => {
                  triggerConfetti()
                  setTimeout(() => {
                    window.location.href = "/contact"
                  }, 500)
                }}
              >
                Contactez nous
              </MagneticButton>
              <Button
                variant="ghost"
                size="icon"
                className={`md:hidden ml-2 ${mobileMenuOpen  ? "text-podspace-white bg-podspace-dark/90" : "text-podspace-white hover:text-podspace-orange"}`}
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                aria-expanded={mobileMenuOpen}
                aria-controls="mobile-menu"
                aria-label={mobileMenuOpen ? "Fermer le menu" : "Ouvrir le menu"}
              >
                {mobileMenuOpen ? (
                  <X className="h-5 w-5 text-podspace-white" aria-hidden="true" />
                ) : (
                  <Menu className="h-5 w-5" aria-hidden="true" />
                )}
              </Button>
            </div>
          </div>
        </header>
        {mobileMenuOpen && (
          <div id="mobile-menu" className="fixed inset-0 z-30 bg-podspace-dark pt-16 px-4 pb-4 md:hidden">
            <nav className="flex flex-col space-y-4" aria-label="Navigation mobile">
              <Link
                href="/#studios"
                className="text-lg font-medium py-2 border-b border-podspace-orange/10 hover:text-podspace-orange"
                style={{ animationDelay: "100ms" }}
                onClick={() => setMobileMenuOpen(false)}
              >
                Studios
              </Link>
              <Link
                href="/service-pour-les-professionnels"
                className="text-lg font-medium py-2 border-b border-podspace-orange/10 hover:text-podspace-orange"
                style={{ animationDelay: "150ms" }}
                onClick={() => setMobileMenuOpen(false)}
              >
                Service pour les Pros
              </Link>
              <Link
                href="/fonctionnement-podspace"
                className="text-lg font-medium py-2 border-b border-podspace-orange/10 hover:text-podspace-orange"
                style={{ animationDelay: "200ms" }}
                onClick={() => setMobileMenuOpen(false)}
              >
                Comment ça marche ?
              </Link>
              <Link
                href="/#pricing"
                className="text-lg font-medium py-2 border-b border-podspace-orange/10 hover:text-podspace-orange"
                style={{ animationDelay: "250ms" }}
                onClick={() => setMobileMenuOpen(false)}
              >
                Tarifs
              </Link>
              {/* <Link
                href="/creez-avec-carl"
                className="text-lg font-medium py-2 border-b border-podspace-orange/10 hover:text-podspace-orange"
                style={{ animationDelay: "300ms" }}
                onClick={() => setMobileMenuOpen(false)}
              >
                Créez avec Carl
              </Link> */}

              <Button
                className="mt-4 w-full bg-podspace-orange hover:bg-podspace-orange/90 text-podspace-white"
                style={{ animationDelay: "450ms" }}
                onClick={() => {
                  triggerConfetti()
                  setMobileMenuOpen(false)
                  setTimeout(() => {
                    window.location.href = "/contact"
                  }, 500)
                }}
              >
                Contactez nous
              </Button>
            </nav>
          </div>
        )}
      {/* Hero Section */}
      <section className="relative w-full min-h-[60vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-b from-podspace-dark/90 via-podspace-dark/70 to-podspace-dark z-10"></div>
          <Image
            src="/images/pro/pro.png"
            alt="Services professionnels de production de contenu"
            fill
            className="object-cover"
            priority
          />
        </div>

        <div className="container relative z-20 px-4 md:px-6 flex flex-col items-center justify-center text-center">
          <div className="max-w-3xl mx-auto">
           
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold tracking-tight mb-6">
              Podspace et les Professionnels ?
            </h1>

            <div className="text-xl md:text-2xl text-podspace-white/80 mb-8">
              Podspace transforme vos idées en histoires qui marquent, engagent et valorisent votre marque.
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                className="bg-podspace-orange hover:bg-podspace-orange/90 text-podspace-white"
                onClick={() => scrollToContactSection()}
              >
                <span className="flex items-center gap-2">
                  <span>Demander un devis</span>
                  <ArrowRight className="h-4 w-4" />
                </span>
              </Button>

              <Button
                onClick={scrollToNextSection}
                size="lg"
                variant="outline"
                className="border-podspace-orange/20 text-podspace-white hover:bg-podspace-orange/10 hover:border-podspace-orange/50"
              >
                Activer votre potentiel média
              </Button>
            </div>
          </div>
        </div>
      </section>

      <WaveSeparator />

      {/* Services Section */}
      <section className="w-full py-12 md:py-24 lg:py-32 bg-podspace-dark relative overflow-hidden" id="services">
        <div className="container px-4 md:px-6 relative z-10">
          <div className="flex flex-col items-center justify-center space-y-4 text-center mb-12">
            <div className="space-y-2">
              <h2 className="text-3xl font-bold tracking-tighter sm:text-5xl">Pourquoi créer du contenu ?</h2>
              <p className="max-w-[900px] text-podspace-white/80 md:text-xl/relaxed">
Le contenu professionnel n’est plus un bonus : c’est un levier stratégique. <br/>Que ce soit pour renforcer votre image, attirer des clients ou engager vos équipes, il donne vie à vos messages.              </p>
            </div>
          </div>

          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {/* Service 1 */}
            <div className="bg-podspace-dark/50 backdrop-blur-sm border border-podspace-orange/10 rounded-xl p-6 h-full flex flex-col">
              <div className="text-3xl mb-4">🎯</div>
              <h3 className="text-xl font-bold mb-2">Renforcez votre image de marque</h3>
              <p className="text-podspace-white/70 mb-4">
                Positionnez-vous comme une marque forte, cohérente et humaine.
              </p>
              <div className="mt-auto">
                <h4 className="text-sm font-medium mb-2 text-podspace-white/90">Ce que nous proposons:</h4>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2 text-sm">
                    <CheckCircle className="h-4 w-4 text-podspace-orange shrink-0 mt-0.5" />
                    <span className="text-podspace-white/70">Exprimez vos valeurs avec sincérité pour créer un lien fort et durable avec votre audience.</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm">
                    <CheckCircle className="h-4 w-4 text-podspace-orange shrink-0 mt-0.5" />
                    <span className="text-podspace-white/70">Créez une présence forte et cohérente sur tous vos canaux</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm">
                    <CheckCircle className="h-4 w-4 text-podspace-orange shrink-0 mt-0.5" />
                    <span className="text-podspace-white/70">Valorisez votre produit, votre expertise et vos équipes</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Service 2 */}
            <div className="bg-podspace-dark/50 backdrop-blur-sm border border-podspace-orange/10 rounded-xl p-6 h-full flex flex-col">
              <div className="text-3xl mb-4">🚀</div>
              <h3 className="text-xl font-bold mb-2">Boostez votre visibilité et vos ventes</h3>
              <p className="text-podspace-white/70 mb-4">
                La video / le podcast est efficace pour toucher votre public cible.
              </p>
              <div className="mt-auto">
                <h4 className="text-sm font-medium mb-2 text-podspace-white/90">Ce que nous proposons:</h4>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2 text-sm">
                    <CheckCircle className="h-4 w-4 text-podspace-orange shrink-0 mt-0.5" />
                    <span className="text-podspace-white/70">Transformer votre communication en vidéo, pour révéler tout le potentiel de votre histoire.</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm">
                    <CheckCircle className="h-4 w-4 text-podspace-orange shrink-0 mt-0.5" />
                    <span className="text-podspace-white/70">Intégrez vos messages dans des podcasts à forte audience et croissante</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm">
                    <CheckCircle className="h-4 w-4 text-podspace-orange shrink-0 mt-0.5" />
                    <span className="text-podspace-white/70">Transformez la publicité en narration engageante</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Service 3 */}
            <div className="bg-podspace-dark/50 backdrop-blur-sm border border-podspace-orange/10 rounded-xl p-6 h-full flex flex-col">
              <div className="text-3xl mb-4">🤝</div>
              <h3 className="text-xl font-bold mb-2">Engagez vos collaborateurs et partenaires</h3>
              <p className="text-podspace-white/70 mb-4">
                Un bon contenu, c’est aussi un outil puissant pour engager en interne.
              </p>
              <div className="mt-auto">
                <h4 className="text-sm font-medium mb-2 text-podspace-white/90">Ce que nous proposons:</h4>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2 text-sm">
                    <CheckCircle className="h-4 w-4 text-podspace-orange shrink-0 mt-0.5" />
                    <span className="text-podspace-white/70">Fédérez en interne autour d’un message clair</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm">
                    <CheckCircle className="h-4 w-4 text-podspace-orange shrink-0 mt-0.5" />
                    <span className="text-podspace-white/70">Mettez en valeur vos talents (CV vidéo, témoignages…)</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm">
                    <CheckCircle className="h-4 w-4 text-podspace-orange shrink-0 mt-0.5" />
                    <span className="text-podspace-white/70">Facilitez la communication RH et la culture d’entreprise</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      <WaveSeparator inverted={true} />


      {/* Contact Section */}

      <footer className="w-full border-t border-podspace-orange/10 bg-podspace-dark py-6">
        <div className="container flex flex-col items-center justify-center gap-6 px-4 md:px-6 md:flex-row md:justify-between">
          <div className="flex gap-2 items-center text-xl font-bold">
            <div className="flex gap-2 items-center text-xl font-bold">
              <span className="relative flex items-center ">
                P
                <img alt="logo-podspace" src="/logo.svg" width="15px"/>
                <span>dspace</span>
                <span className="text-podspace-orange">.</span>
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-podspace-orange transition-all duration-300 group-hover:w-full"></span>
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2 text-podspace-white/70">
            <MapPin className="h-4 w-4 text-podspace-orange" />
            <span className="text-sm">À 2 min de la gare Lille Flandres</span>
          </div>
          <p className="text-sm text-podspace-white/70 text-center md:text-left">
            © {new Date().getFullYear()} Podspace. Tous droits réservés.
          </p>
        </div>
      </footer>
    </div>
  )
}
